import { ISectionLogger } from "@multiweb/logging";
import { OWDataType } from "@multiweb/types/contracts";
import { IItemRightsCollection } from "../../../../Global/Base/Rights/RightTypes";
import { Localization } from "../../../../OW.Remoting/Remoting/Localization";
import { PopupMenu } from "../PopupMenu";
import { IMaxLength } from "./ControlInterfaces";
import { EditableControl } from "./EditableControl";

export type ContainerEditableControlParams = {
    elementCreator?: new() => HTMLInputElement;
    copyAttrs?: Map<string, string>;
    onSetDataType?: (element: HTMLElement, dataType: OWDataType | string) => void;
    parentElement: HTMLElement;
    originalRightsDefinition: IItemRightsCollection;
    attrSourceElement?: HTMLElement;
    logger: ISectionLogger;
};

export class ContainerEditableControl extends EditableControl implements IMaxLength {
    //#region Fields
    public override innerElement: HTMLInputElement;

    protected _value: string;

    protected _validationString: string;
    protected _validationExpr: RegExp;
    private _attrSourceElement: HTMLElement;
    private _elementCreator: new() => HTMLInputElement;
    private _copyAttrs: Map<string, string>;
    private readonly _logger: ISectionLogger;

    //Listeners
    private _listenerFocus: EventListener;
    private _listenerBlur: EventListener;
    private _listenerClick: EventListener;
    private _listenerChange: EventListener;
    private _listenerDblclick: EventListener;
    private _listenerPaste: EventListener;
    //#endregion Fields

    //#region Constructors
    constructor({ elementCreator, copyAttrs, onSetDataType, parentElement, originalRightsDefinition, logger, attrSourceElement = null }: ContainerEditableControlParams) {
        super({ element: parentElement, originalRightsDefinition });
        this._logger = logger;

        this._onSetDataType = onSetDataType;
        if (elementCreator) {
            this._elementCreator = elementCreator;
        }
        this._copyAttrs = copyAttrs ? copyAttrs : new Map();
        // pro kopírování atributů se použije attrSourceElement pokud je specifikovaný, jinak se vezme rodičovský element - často mohou být různé a takto je možné je rozlišit
        this._attrSourceElement = attrSourceElement != null ? attrSourceElement : parentElement;
        this.element.classList.add("Edit");
        this.maxLength = this.getNumDataAttr("maxLength");
        this._validationString = this.getDataAttr("ValidationExpr");

        if (this._validationString) {
            try {
                this._validationExpr = new RegExp(this._validationString);
            } catch (err) {
                this._validationExpr = null;
                this._logger.warn(`Invalid validation pattern '${this._validationString} on component '${this.componentName}'`);
            }
        }

        this._createInput();
        this.setListeners();
        this._setCaptionReference(this.innerElement);
    }
    //#endregion Constructors

    //#region Properties
    //#region controlValue
    public override get controlValue(): string {
        return this.innerElement.value;
    }

    /**
     * Nastavi hodnotu editu
     */
    public override set controlValue(value: string) {
        const el = this.innerElement;

        if (this.maxLength && value && value.length > this.maxLength) {
            el.value = value.substr(0, this.maxLength);
            this._alertOnLength();
        } else {
            el.value = value;
        }
        this.validate();
    }
    //#endregion controlValue

    protected _maxLength: number;

    public get maxLength(): number {
        return this._maxLength;
    }

    public set maxLength(value: number) {
        if (value && value > 0 && !this._maxLength) {
            this._maxLength = value;
            if ((this._maxLength || this.maxLength == 0) && this.innerElement) {
                this.innerElement.maxLength = this.maxLength;
            }
        }
    }
    //#endregion Properties

    //#region Methods

    /** Pokud je zadan regexp, pak validuje podle nej, jinak vraci true. */
    public override validate(): boolean {
        const el = this.innerElement;

        if (el.value.length === 0 || el.checkValidity()) {
            this.setValid();
            return true;
        } else {
            this.setInvalid(null, el.validationMessage);
            return false;
        }
    }

    public override setValid(): void {
        super.setValid();
    }

    /** Vytvori samotny input */
    protected _createInput(): void {
        const input = new this._elementCreator();

        for (const attr of Array.from(this._attrSourceElement.attributes)) {
            if (this._copyAttrs.has(attr.name)) {
                input.setAttribute(this._copyAttrs.get(attr.name), attr.value);
            }
        }

        if (this._placeholderLocKey) {
            Localization.addLocTxtData(input, this._placeholderLocKey, "placeholder");
        }

        this.innerElement = input;
        this.element.appendChild(this.innerElement);

        if (this._language != null)
            this.innerElement.setAttribute("lang", this._language); //nastaveni jazyka pro kontrolu pravopisu language/data-lang
    }

    public setListeners(): void {
        this.innerElement.addEventListener("focus", this._listenerFocus = (e: FocusEvent) => {
            this.getFocus.trigger(this, e);
            this._focusEventHandler();
        });

        this.innerElement.addEventListener("blur", this._listenerBlur = (e: FocusEvent) => {
            this.lostFocus.trigger(this, e);
            this._blurEventHandler();
        });

        this.innerElement.addEventListener("change", this._listenerChange = () => {
            this._changeEventHandler();
        });

        if (this.maxLength) {
            // vyhazovani hlasky pri preteceni pri paste
            this.innerElement.addEventListener("paste", this._listenerPaste = () => {
                this._pasteEventHandler(event);
            });
        }
    }

    // oriznuti paste pro IE 10
    private _cutText(): void {
        if (this.innerElement.value.length > this.maxLength) {
            // nepouzije se oriznuti konce celeho textu, ale standardni chovani paste do omezeneho inputu = urizne se konec pastovaneho textu
            const pos = this._getCaretPosition();
            this.innerElement.value = this.innerElement.value.slice(0, pos - 1) + this.innerElement.value.slice(pos);
            this._alertOnLength();
        }
        this.innerElement.maxLength = this.maxLength;
    }

    /* Returns the caret (cursor) position of the specified text field.
     * Return value range is 0-oField.value.length.
     * http://stackoverflow.com/questions/2897155/get-cursor-position-within-a-text-input-field
     */
    private _getCaretPosition(): number {
        const iCaretPos = this.innerElement.value.length; // priradime na konec, v pripade ze neni podpora document.selection tak se vrati maxlength + 1

        return iCaretPos;
    }

    private _alertOnLength(): void {
        if (!this._errorPopup)
            this._errorPopup = new PopupMenu(this._errorElement, false, this.element.clientWidth + "px", "auto");

        if (this._errorElement.textContent.indexOf(":") == 0) {
            const spanErr = document.createElement("span"); //kvuli lokalizaci je pred cislo maximalni delky retezce pridan element s lok.popiskem
            Localization.addLocData(spanErr, "GUI-452712");
            this._errorElement.appendChild(spanErr);
            this._errorElement.textContent = this.maxLength + "";
        } else {
            const locText = this._errorElement.textContent.split(":");
            this._errorElement.textContent = locText + ": " + this.maxLength;
        }

        this._errorElement.style.display = "block";
        this._errorPopup.setVisibility(true);

        self.setTimeout(() => {
            if (this.validate()) {
                this._errorPopup.setVisibility(false);
                this._errorElement.style.display = "none";
            }
        }, 5000);
    }

    //events handlers
    protected _focusEventHandler(): void {
        if (this.enabled)
            this.element.classList.add("focused");
    }

    protected _blurEventHandler(): void {
        this.element.classList.remove("focused");
    }

    protected _changeEventHandler(): void {
        // pokud komponenta není ve validním stavu, tak se její změna dál nepropaguje
        if (!this.validate())
            return;

        this.valueChanged.trigger(this, { Sender: this });
    }

    protected _pasteEventHandler(e: Event): void {
        const ev: ClipboardEvent = e as ClipboardEvent;
        if (ev.clipboardData) {
            const newDataLen = ev.clipboardData.getData("text/plain").length;
            if (newDataLen + this.innerElement.value.length > this.maxLength) {
                self.setTimeout(() => {
                    this._alertOnLength();
                });
            }
        } else { // IE 10
            this.innerElement.maxLength = this.maxLength + 1;
            self.setTimeout(() => {
                this._cutText();
            }, 0);
        }
    }

    // slouzi pro nastaveni property na komponente pri zmene DataType
    private _onSetDataType: (element: HTMLElement, dataType: OWDataType | string) => void;

    public override get dataType(): OWDataType | string {
        return super.dataType;
    }

    public override set dataType(value: OWDataType | string) {
        super.dataType = value;
        if (this.innerElement && this._onSetDataType) {
            this._onSetDataType(this.innerElement, value);
        }
    }

    //destruktor
    public override dispose(): void {
        if (this.innerElement != null) {
            this.innerElement.removeEventListener("focus", this._listenerFocus);
            this.innerElement.removeEventListener("blur", this._listenerBlur);
            this.innerElement.removeEventListener("click", this._listenerClick);
            this.innerElement.removeEventListener("change", this._listenerChange);
            this.innerElement.removeEventListener("dblclick", this._listenerDblclick);
            this.innerElement.removeEventListener("paste", this._listenerPaste);
        }

        super.dispose();
    }

    //#endregion Methods
}
